import { type TypedDocumentNode, useQuery } from '@apollo/client';
import { descriptionFromRecords } from '@shared/core/product/description';
import type { PricingSheetEntriesQuery, PricingSheetEntriesQueryVariables } from 'types/graphql';

export const PRICING_SHEET_ENTRIES_QUERY: TypedDocumentNode<
  PricingSheetEntriesQuery,
  PricingSheetEntriesQueryVariables
> = gql`
  query PricingSheetEntriesQuery($pricingSheetId: Int!) {
    pricingSheetEntries(pricingSheetId: $pricingSheetId) {
      id
      pricingFormula
      pricingValue
      roundingValue
      note
      product {
        id
        productCode
        costBasis
        commodity {
          name
        }
        commodityStyle {
          name
        }
        commoditySize {
          name
        }
        commodityUnit {
          name
        }
        commodityLabel {
          name
        }
      }
    }
  }
`;

type PricingSheetEntryResult = PricingSheetEntriesQuery['pricingSheetEntries'][number];

export type PricingSheetEntry = Omit<PricingSheetEntryResult, 'product'> & {
  productId: number;
  productCode: PricingSheetEntryResult['product']['productCode'];
  productName: string;
  costBasis: PricingSheetEntryResult['product']['costBasis'];
};

export function toPricingSheetEntry({ product, ...entry }: PricingSheetEntryResult): PricingSheetEntry {
  return {
    ...entry,
    costBasis: product.costBasis,
    productId: product.id,
    productCode: product.productCode,
    productName: descriptionFromRecords({
      commodity: {
        name: product.commodity?.name,
        style: product.commodityStyle?.name,
        label: product.commodityLabel?.name,
        size: product.commoditySize?.name,
      },
      countryOfOrigin: null,
    }),
  };
}

export default function usePricingSheetEntries(pricingSheetId?: number): PricingSheetEntry[] {
  const { data } = useQuery(PRICING_SHEET_ENTRIES_QUERY, {
    variables: { pricingSheetId: pricingSheetId ?? 0 },
    fetchPolicy: 'cache-first',
    skip: !pricingSheetId,
  });

  return (data?.pricingSheetEntries ?? []).map(toPricingSheetEntry);
}
